import {useCallback, useMemo, useState} from 'react';

import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {
  PAYMENT_LINK_V2_FEATURES,
  PAYMENT_LINK_V2_QUERY_PARAMS,
} from '@/app/main/paymentLink/v2/pages/paymentLinkV2/models/constants/constants';
import {AppButton, ButtonContainer} from '@/app/shared-components';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {usePaymentLinkV2Repository} from '@/dataSource/src/api/payment-link/v2/useRepository';

import {
  ESplitBillBalanceMode,
  IBalanceItemMemberUI,
  IBalancesUI,
  IGroupParticipant,
  ISettlementMember,
} from '../../models/interfaces';
import {useGroupsTrackers} from '../../trackers';
import {GroupEmptyList} from '../GroupEmptyList/GroupEmptyList';
import {GroupMemberItem} from '../GroupMemberItem/GroupMemberItem';
import {SettleBalanceModal} from '../SettleBalanceModal';
import {SettleBalanceModalButtonProps} from '../SettleBalanceModal/interfaces';

interface GroupsDashboardBalanceDetailProps {
  groupCurrentParticipant: IGroupParticipant;
  ownerGroupParticipant: IGroupParticipant;
  oweMode: ESplitBillBalanceMode;
  balances: IBalancesUI;
  onSettle: (members: ISettlementMember[]) => Promise<void>;
}

export const GroupsDashboardBalanceDetail = ({
  groupCurrentParticipant,
  ownerGroupParticipant,
  oweMode,
  balances,
  onSettle,
}: GroupsDashboardBalanceDetailProps) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    trackSelectSplitsSettleOneDebt,
    trackSelectSplitsSettleAll,
    trackSelectSplitsSettleMarkAsPaid,
    trackSelectSplitsSettleAllConfirmReceived,
    trackSelectSplitsSettleAllMarkAsPaid,
    trackSelectSplitsSettleConfirmReceived,
    trackSelectSplitsSettlePayMember,
  } = useGroupsTrackers(
    groupCurrentParticipant?.groupId,
    ownerGroupParticipant?.modakUserId,
    groupCurrentParticipant?.modakUserId,
  );

  const {handlerGetDefaultPaymentLinkByUserId} = usePaymentLinkV2Repository();

  const [openSettleModal, setOpenSettleModal] = useState(false);
  const [selectedMember, setSelectedMember] =
    useState<IBalanceItemMemberUI | null>(null);

  const balancesToShow = useMemo(() => {
    return oweMode === ESplitBillBalanceMode.OWED
      ? balances.owed.members
      : balances.owe.members;
  }, [oweMode, balances]);

  const handleOnClickSettle = useCallback(
    (member: IBalanceItemMemberUI) => {
      trackSelectSplitsSettleOneDebt();
      setSelectedMember(member);
      setOpenSettleModal(true);
    },
    [trackSelectSplitsSettleOneDebt],
  );

  const handleCloseModal = useCallback(() => {
    setOpenSettleModal(false);
    setSelectedMember(null);
  }, []);

  const buildSettlementMember = useCallback(
    (member: {id: string; amount: number}): ISettlementMember => {
      const isOwed = oweMode === ESplitBillBalanceMode.OWED;
      return {
        from: isOwed ? member.id : groupCurrentParticipant.splitUserId,
        to: isOwed ? groupCurrentParticipant.splitUserId : member.id,
        amount: member.amount,
        settled_by: groupCurrentParticipant.splitUserId,
      };
    },
    [oweMode, groupCurrentParticipant.splitUserId],
  );

  const handleOnClickSettleAll = useCallback(async () => {
    if (balancesToShow.length === 0) return;
    trackSelectSplitsSettleAll();
    if (oweMode === ESplitBillBalanceMode.OWE) {
      trackSelectSplitsSettleAllMarkAsPaid();
    } else {
      trackSelectSplitsSettleAllConfirmReceived();
    }
    await onSettle(balancesToShow.map(buildSettlementMember));
  }, [
    balancesToShow,
    trackSelectSplitsSettleAll,
    oweMode,
    onSettle,
    buildSettlementMember,
    trackSelectSplitsSettleAllMarkAsPaid,
    trackSelectSplitsSettleAllConfirmReceived,
  ]);

  const handleOnClickMarkAsPaid = useCallback(async () => {
    if (!selectedMember) return;
    if (oweMode === ESplitBillBalanceMode.OWE)
      trackSelectSplitsSettleMarkAsPaid();
    else trackSelectSplitsSettleConfirmReceived();
    await onSettle([buildSettlementMember(selectedMember)]);
    handleCloseModal();
  }, [
    buildSettlementMember,
    handleCloseModal,
    onSettle,
    oweMode,
    selectedMember,
    trackSelectSplitsSettleConfirmReceived,
    trackSelectSplitsSettleMarkAsPaid,
  ]);

  const handleOnClickPayMember = useCallback(async () => {
    if (!selectedMember || !selectedMember.modakUserId) return;
    trackSelectSplitsSettlePayMember();
    const paymentLinkId = await handlerGetDefaultPaymentLinkByUserId(
      selectedMember.modakUserId,
    );
    if (!paymentLinkId) {
      showSnackbar({
        summary: t('groups.dashboard.payment_link_error_title'),
        message: t('groups.dashboard.payment_link_error_message', {
          name: selectedMember.name,
        }),
      });
    }
    searchParams.set(
      PAYMENT_LINK_V2_QUERY_PARAMS.BASE_AMOUNT,
      selectedMember.amount.toString(),
    );

    searchParams.set(
      PAYMENT_LINK_V2_QUERY_PARAMS.FROM_PARTICIPANT_ID,
      groupCurrentParticipant.splitUserId,
    );

    searchParams.set(
      PAYMENT_LINK_V2_QUERY_PARAMS.TO_PARTICIPANT_ID,
      selectedMember.id,
    );

    searchParams.set(
      PAYMENT_LINK_V2_QUERY_PARAMS.FEATURE,
      PAYMENT_LINK_V2_FEATURES.GROUPS,
    );
    const paymentLinkUrl = `${paymentLinkId}?${searchParams.toString()}`;
    navigate(`/payment-link/v2/${paymentLinkUrl}`);
    handleCloseModal();
  }, [
    selectedMember,
    trackSelectSplitsSettlePayMember,
    handlerGetDefaultPaymentLinkByUserId,
    searchParams,
    groupCurrentParticipant.splitUserId,
    navigate,
    handleCloseModal,
    t,
  ]);

  const title = useMemo(() => {
    if (oweMode === ESplitBillBalanceMode.OWED) {
      return t('groups.dashboard.owed_title');
    }
    return t('groups.dashboard.owe_title');
  }, [oweMode, t]);

  const totalAmount = useMemo(() => {
    const amount =
      oweMode === ESplitBillBalanceMode.OWED
        ? balances.owed.amount
        : balances.owe.amount;
    return amount.toFixed(2);
  }, [oweMode, balances]);

  const buttonMarkAsPaid: SettleBalanceModalButtonProps = useMemo(() => {
    if (!selectedMember) return null;
    return {
      label: t('groups.dashboard.mark_as_paid', {
        amount: selectedMember?.amount,
      }),
      onClick: () => {
        handleOnClickMarkAsPaid();
      },
    };
  }, [handleOnClickMarkAsPaid, selectedMember, t]);

  const buttonPayMember: SettleBalanceModalButtonProps = useMemo(() => {
    if (!selectedMember || !selectedMember.modakUserId) return null;
    return {
      label: t('groups.dashboard.pay_member', {
        name: selectedMember?.name,
      }),
      onClick: () => {
        handleOnClickPayMember();
      },
    };
  }, [handleOnClickPayMember, selectedMember, t]);

  const primaryButton: SettleBalanceModalButtonProps = useMemo(() => {
    let button: SettleBalanceModalButtonProps | null = null;
    if (oweMode === ESplitBillBalanceMode.OWE) {
      button = buttonPayMember;
    }
    if (!button) {
      button = buttonMarkAsPaid;
    }
    return button;
  }, [buttonMarkAsPaid, buttonPayMember, oweMode]);

  const secondaryButton: SettleBalanceModalButtonProps = useMemo(() => {
    let button: SettleBalanceModalButtonProps | null = null;
    if (oweMode === ESplitBillBalanceMode.OWED) {
      button = null;
    }
    button = buttonMarkAsPaid;
    if (button === primaryButton) {
      button = null;
    }
    return button;
  }, [buttonMarkAsPaid, oweMode, primaryButton]);
  return (
    <>
      <Typography className="groups-dashboard-balance-detail-title">
        {title}
        <span
          className={
            oweMode === ESplitBillBalanceMode.OWED
              ? 'groups-dashboard-balance-detail-title owed'
              : 'groups-dashboard-balance-detail-title owe'
          }>
          ${totalAmount}
        </span>
      </Typography>
      <Box className="groups-dashboard-members-list">
        {balancesToShow.length === 0 ? (
          <GroupEmptyList
            text={
              oweMode === ESplitBillBalanceMode.OWED
                ? t('groups.dashboard.empty_owed')
                : t('groups.dashboard.empty_owe')
            }
          />
        ) : (
          balancesToShow.map(member => (
            <GroupMemberItem
              key={member.id}
              name={
                oweMode === ESplitBillBalanceMode.OWED
                  ? member.name
                  : groupCurrentParticipant.name
              }
              memberNameRelated={
                oweMode === ESplitBillBalanceMode.OWED
                  ? groupCurrentParticipant.name
                  : member.name
              }
              isYou={false}
              isOwner={member.id === ownerGroupParticipant.splitUserId}
              description={`$${member.amount.toFixed(2)}`}
              button={{
                label: t('groups.dashboard.settle'),
                onClick: () => handleOnClickSettle(member),
              }}
            />
          ))
        )}
      </Box>
      <ButtonContainer sticky>
        <AppButton
          id="group-dashboard-settle-all"
          label={t('groups.dashboard.settle_all')}
          onClick={handleOnClickSettleAll}
        />
      </ButtonContainer>

      <SettleBalanceModal
        open={openSettleModal}
        onClose={handleCloseModal}
        title={t('groups.dashboard.settle_title')}
        participantSummary={
          oweMode === ESplitBillBalanceMode.OWED
            ? t('groups.dashboard.member_owes_you', {
                name: selectedMember?.name,
              })
            : t('groups.dashboard.you_owe_member', {name: selectedMember?.name})
        }
        avatarName={
          oweMode === ESplitBillBalanceMode.OWED
            ? selectedMember?.name
            : groupCurrentParticipant.name
        }
        memberNameRelated={
          oweMode === ESplitBillBalanceMode.OWED
            ? groupCurrentParticipant.name
            : selectedMember?.name
        }
        amountLabel={`$${selectedMember?.amount.toFixed(2)}`}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
      />
    </>
  );
};
